// Formatting for dependent screens.
//
// Dates arrive as epoch milliseconds (`startAt`), never as a rendered string.
//
// An appointment time is NOT a display preference. "Tuesday at 9" is a fact
// about the clinic's clock — it is when the door will be open and when somebody
// will be waiting — so it is rendered in the clinic's zone, which the server
// sends alongside the list. A guardian reading their child's appointments from
// another province in their OWN zone is being shown an hour that nobody at the
// clinic would recognise, and the browser has no way to know it is wrong.
//
// The zone is optional here only because a caller may not have it yet; when it
// is absent the browser's own zone is used, which is the old behaviour and the
// reason this note exists.

import {APP_LOCALE} from "../../utils/appLocale";
import {
  PATIENT_MEDICAL_LIST_FIELDS,
  toMedicalList,
  medicalListItemLabel,
} from "../../lib/patientMedicalLists";

// The app's language, not the browser's.
//
// `toLocaleDateString(undefined, ...)` follows the READER's machine, and on a
// Spanish-configured browser a shared note dated itself "9 sept 2026" in the
// middle of English copy. This is the same rule `components/ui/Money` already
// pins for currency, and for the same reason: a guardian and the patient
// reading the same screen must not see the same fact written two ways.
//
// `dateStyle: "medium"` spells the month, so the result is also unambiguous in
// every country — "Jun 12, 2015" cannot be read as the 6th of December.
// Re-exported so the dependent screens keep one import.
export {APP_LOCALE} from "../../utils/appLocale";
const DATE_TIME = {dateStyle: "medium", timeStyle: "short"};
const DATE_ONLY = {dateStyle: "medium"};

/**
 * An instant, on a named clock.
 *
 * `withZoneName` appends the zone's short name — "PDT", "ADT" — and exists for
 * the one case where the hour alone is ambiguous: a list that spans clinics in
 * more than one province. Two rows reading "11:15 a.m." and "7:15 a.m." are
 * both correct and look like a contradiction until the clock is named.
 * Off by default, because on a single-site list it is noise.
 */
export function formatDateTime(ms, timeZone, {withZoneName = false} = {}) {
  if (!ms && ms !== 0) return "—";
  const value = new Date(Number(ms));
  if (Number.isNaN(value.getTime())) return "—";
  const formatted = value.toLocaleString(APP_LOCALE, {
    ...DATE_TIME,
    ...(timeZone ? {timeZone} : {}),
  });
  // Appended rather than requested inline: `timeZoneName` cannot be combined
  // with `dateStyle`/`timeStyle`, and asking for it that way THROWS. Doing it
  // in two steps also keeps the hour formatted identically whether or not the
  // clock is named — a row that changes shape when a second site appears in the
  // list is a row a reader has to re-learn.
  //
  // Only when there is a zone to name: without one this would print the
  // READER's, which is the very thing these screens must not assert.
  if (!withZoneName || !timeZone) return formatted;
  const name = zoneShortName(value, timeZone);
  return name ? `${formatted} ${name}` : formatted;
}

function zoneShortName(value, timeZone) {
  try {
    return new Intl.DateTimeFormat(APP_LOCALE, {timeZone, timeZoneName: "short"})
      .formatToParts(value)
      .find((part) => part.type === "timeZoneName")?.value;
  } catch {
    // An unknown zone string must not take the hour down with it.
    return null;
  }
}

// A DATE with no time in it: `2015-06-12` or `2015/06/12`, and nothing more.
const DATE_ONLY_STRING = /^\d{4}[-/]\d{2}[-/]\d{2}$/;

export function formatDate(value) {
  if (!value && value !== 0) return "—";

  if (typeof value === "string") {
    // A date of birth is a DATE, not an instant. Parsing `2015-06-12` gives UTC
    // midnight, which renders as the 11th for everybody west of Greenwich —
    // the class of defect that once moved 451 of them a day backwards.
    //
    // So the parts are read as written and rebuilt at LOCAL noon: no zone can
    // move a date twelve hours from midday, and the reader gets a spelled month
    // instead of "2015/06/12", which is three different dates depending on who
    // is looking at it.
    if (DATE_ONLY_STRING.test(value)) {
      const [y, m, d] = value.split(/[-/]/).map(Number);
      return new Date(y, m - 1, d, 12).toLocaleDateString(APP_LOCALE, DATE_ONLY);
    }

    // Anything else IS an instant, and used to fall through the same branch:
    // `assignedDate` is a full ISO timestamp, so a parent read
    // "Assigned 2025/01/22T23:23:08.362Z" on their child's forms.
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(APP_LOCALE, DATE_ONLY);
  }

  const date = new Date(Number(value));
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(APP_LOCALE, DATE_ONLY);
}

// Money is NOT formatted here. `components/ui/Money` already pins the app's
// convention — en-CA, two decimals, cents in — and a second formatter would
// mean a guardian and the patient reading the same balance see it written
// differently. Import `formatMoneyCents` from there.

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = Number(bytes);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

/**
 * One stored value, as a sentence a parent can read — or nothing at all.
 *
 * Lives here rather than inside the profile screen because it is a fact about
 * DATA, not about a component: no React, and testable without mounting a tree.
 *
 * The medical lists go through the canonical reader rather than a fourth
 * private copy of it. `medications` and `allergies` are lists of `{name, note}`,
 * and the row a clinic actually has in production is sometimes
 * `{name: "", note: "<the whole free-text answer>"}` — which
 * `medicalListItemLabel` knows about and a naive `item.name` does not.
 *
 * The array branch used to run FIRST and `join(", ")` an array of OBJECTS, so a
 * parent opening their child's profile read `Medications: [object Object],
 * [object Object]`. That exact mistake — `[{name}].join(', ')` — has been made
 * in this codebase before, in the autofilled PDFs, which is why the shared
 * module exists.
 *
 * Anything still not renderable is shown as NOTHING rather than as a guess. A
 * screen about somebody's child is the wrong place to print a shape.
 */
export function renderValue(key, value) {
  if (PATIENT_MEDICAL_LIST_FIELDS.includes(key)) {
    const labels = toMedicalList(value)
      .map((item) => medicalListItemLabel(item, ""))
      .filter(Boolean);
    return labels.length > 0 ? labels.join(", ") : null;
  }
  if (Array.isArray(value)) {
    const parts = value.filter((item) => item !== null && item !== undefined && item !== "");
    if (parts.some((item) => typeof item === "object")) return null;
    return parts.join(", ") || null;
  }
  if (value && typeof value === "object") return null;
  return value;
}

/** `emergencyContactName` -> `Emergency contact name`. */
export function humanizeField(field) {
  const spaced = String(field)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
